import { useEffect, useRef, useState } from 'react';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved';

/**
 * Déclenche `saveFn` avec un debounce de 1500 ms à chaque appel de `trigger()`.
 * Expose un statut pour afficher "Enregistrement…" / "✓ Enregistré" dans l'UI.
 * L'indicateur "saved" reste visible 2 s puis retombe sur "idle".
 *
 * `saveFn` doit être stable (idéalement créé via useCallback côté appelant)
 * pour éviter d'annuler le timer à chaque re-render. On lit la référence
 * courante via un ref interne pour être stable vis-à-vis du timer.
 */
export function useAutoSave(saveFn: () => Promise<void> | void) {
  const [status, setStatus] = useState<AutoSaveStatus>('idle');
  const timer = useRef<number | null>(null);
  const savedTimer = useRef<number | null>(null);
  const latestFn = useRef(saveFn);
  latestFn.current = saveFn;

  const trigger = () => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(async () => {
      timer.current = null;
      setStatus('saving');
      try {
        await latestFn.current();
        setStatus('saved');
        if (savedTimer.current) window.clearTimeout(savedTimer.current);
        savedTimer.current = window.setTimeout(() => setStatus('idle'), 2000);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[autoSave] failed', e);
        setStatus('idle');
      }
    }, 1500);
  };

  useEffect(() => {
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
      if (savedTimer.current) window.clearTimeout(savedTimer.current);
    };
  }, []);

  return { status, trigger };
}
